import React, { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  FileText,
  Search,
  Filter,
  Download,
  Eye,
  Calendar,
  User,
  Shield,
  Database,
  Settings,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Activity,
  Lock,
  RefreshCw,
} from 'lucide-react';
import { useAdminPermissions } from '@/hooks/useAdminPermissions';
import { formatDistanceToNow, format } from 'date-fns';
import { logDebug } from '@/utils/errorHandler';

interface AuditLog {
  id: string;
  timestamp: string;
  userId: string;
  userName: string;
  userEmail: string;
  action: string;
  resource: string;
  resourceId?: string;
  details: Record<string, any>;
  ipAddress: string;
  userAgent: string;
  organizationId: string;
  organizationName: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  category: 'authentication' | 'authorization' | 'data' | 'system' | 'admin';
  status: 'success' | 'failure' | 'warning';
}

interface AuditFilter {
  search: string;
  category: string;
  severity: string;
  status: string;
  userId: string;
  organizationId: string;
  dateRange: string;
}

interface AuditStats {
  totalEvents: number;
  todayEvents: number;
  failureRate: number;
  topActions: Array<{ action: string; count: number }>;
  topUsers: Array<{ userName: string; count: number }>;
}

export function AuditLogs() {
  const { hasPermission } = useAdminPermissions();
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<AuditLog[]>([]);
  const [auditStats, setAuditStats] = useState<AuditStats | null>(null);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [filter, setFilter] = useState<AuditFilter>({
    search: '',
    category: 'all',
    severity: 'all',
    status: 'all',
    userId: 'all',
    organizationId: 'all',
    dateRange: '24h',
  });
  const [loading, setLoading] = useState(true);

  // Mock data - replace with real API calls
  useEffect(() => {
    const mockAuditLogs: AuditLog[] = [
      {
        id: '1',
        timestamp: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
        userId: 'user1',
        userName: 'John Admin',
        userEmail: 'john@kernlogic.com',
        action: 'USER_LOGIN',
        resource: 'authentication',
        details: { loginMethod: 'email', success: true },
        ipAddress: '192.168.1.100',
        userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
        organizationId: 'org1',
        organizationName: 'KernLogic',
        severity: 'low',
        category: 'authentication',
        status: 'success',
      },
      {
        id: '2',
        timestamp: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
        userId: 'user2',
        userName: 'Jane Manager',
        userEmail: 'jane@acmecorp.com',
        action: 'PRODUCT_CREATE',
        resource: 'product',
        resourceId: 'prod_123',
        details: {
          productName: 'New Widget',
          sku: 'WID-001',
          category: 'Electronics',
        },
        ipAddress: '10.0.1.50',
        userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36',
        organizationId: 'org2',
        organizationName: 'Acme Corp',
        severity: 'medium',
        category: 'data',
        status: 'success',
      },
      {
        id: '3',
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
        userId: 'user3',
        userName: 'Bob User',
        userEmail: 'bob@example.com',
        action: 'LOGIN_FAILED',
        resource: 'authentication',
        details: {
          reason: 'Invalid credentials',
          attempts: 3,
          blockedUntil: new Date(Date.now() + 1000 * 60 * 15).toISOString(),
        },
        ipAddress: '203.0.113.100',
        userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
        organizationId: 'org3',
        organizationName: 'Example Inc',
        severity: 'high',
        category: 'authentication',
        status: 'failure',
      },
      {
        id: '4',
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 3).toISOString(),
        userId: 'admin1',
        userName: 'System Admin',
        userEmail: 'admin@kernlogic.com',
        action: 'USER_DELETE',
        resource: 'user',
        resourceId: 'user_456',
        details: {
          deletedUser: 'inactive@test.com',
          reason: 'Account cleanup',
          dataRetention: '90 days',
        },
        ipAddress: '192.168.1.10',
        userAgent: 'Mozilla/5.0 (Linux; Ubuntu) AppleWebKit/537.36',
        organizationId: 'org1',
        organizationName: 'KernLogic',
        severity: 'critical',
        category: 'admin',
        status: 'success',
      },
      {
        id: '5',
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 4).toISOString(),
        userId: 'user4',
        userName: 'Alice Developer',
        userEmail: 'alice@startup.com',
        action: 'API_ACCESS',
        resource: 'api',
        details: {
          endpoint: '/api/v1/products',
          method: 'GET',
          responseCode: 200,
          requestCount: 150,
        },
        ipAddress: '198.51.100.25',
        userAgent: 'API Client/1.0',
        organizationId: 'org4',
        organizationName: 'StartupCo',
        severity: 'low',
        category: 'system',
        status: 'success',
      },
    ];

    const mockStats: AuditStats = {
      totalEvents: 1247,
      todayEvents: 89,
      failureRate: 2.3,
      topActions: [
        { action: 'USER_LOGIN', count: 345 },
        { action: 'PRODUCT_VIEW', count: 234 },
        { action: 'API_ACCESS', count: 156 },
        { action: 'PRODUCT_CREATE', count: 87 },
        { action: 'USER_UPDATE', count: 65 },
      ],
      topUsers: [
        { userName: 'John Admin', count: 89 },
        { userName: 'Jane Manager', count: 67 },
        { userName: 'Bob User', count: 45 },
        { userName: 'Alice Developer', count: 34 },
        { userName: 'System Admin', count: 23 },
      ],
    };

    setTimeout(() => {
      setAuditLogs(mockAuditLogs);
      setFilteredLogs(mockAuditLogs);
      setAuditStats(mockStats);
      setLoading(false);
    }, 1000);
  }, []);

  // Filter audit logs
  useEffect(() => {
    let filtered = auditLogs;

    if (filter.search) {
      filtered = filtered.filter(
        log =>
          log.action.toLowerCase().includes(filter.search.toLowerCase()) ||
          log.userName.toLowerCase().includes(filter.search.toLowerCase()) ||
          log.userEmail.toLowerCase().includes(filter.search.toLowerCase()) ||
          log.resource.toLowerCase().includes(filter.search.toLowerCase()) ||
          log.organizationName.toLowerCase().includes(filter.search.toLowerCase())
      );
    }

    if (filter.category !== 'all') {
      filtered = filtered.filter(log => log.category === filter.category);
    }

    if (filter.severity !== 'all') {
      filtered = filtered.filter(log => log.severity === filter.severity);
    }

    if (filter.status !== 'all') {
      filtered = filtered.filter(log => log.status === filter.status);
    }

    if (filter.userId !== 'all') {
      filtered = filtered.filter(log => log.userId === filter.userId);
    }

    if (filter.organizationId !== 'all') {
      filtered = filtered.filter(log => log.organizationId === filter.organizationId);
    }

    // Date range filtering
    const now = new Date();
    if (filter.dateRange !== 'all') {
      const ranges: Record<string, number> = {
        '1h': 1000 * 60 * 60,
        '24h': 1000 * 60 * 60 * 24,
        '7d': 1000 * 60 * 60 * 24 * 7,
        '30d': 1000 * 60 * 60 * 24 * 30,
      };

      const cutoff = now.getTime() - ranges[filter.dateRange];
      filtered = filtered.filter(log => new Date(log.timestamp).getTime() > cutoff);
    }

    setFilteredLogs(filtered);
  }, [auditLogs, filter]);

  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'low':
        return <Badge className="bg-green-100 text-green-800">Low</Badge>;
      case 'medium':
        return <Badge className="bg-blue-100 text-blue-800">Medium</Badge>;
      case 'high':
        return <Badge className="bg-yellow-100 text-yellow-800">High</Badge>;
      case 'critical':
        return <Badge className="bg-red-100 text-red-800">Critical</Badge>;
      default:
        return <Badge variant="secondary">{severity}</Badge>;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="size-4 text-green-600" />;
      case 'failure':
        return <XCircle className="size-4 text-red-600" />;
      case 'warning':
        return <AlertTriangle className="size-4 text-yellow-600" />;
      default:
        return <Clock className="size-4 text-gray-600" />;
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'authentication':
        return <Lock className="size-4" />;
      case 'authorization':
        return <Shield className="size-4" />;
      case 'data':
        return <Database className="size-4" />;
      case 'system':
        return <Settings className="size-4" />;
      case 'admin':
        return <User className="size-4" />;
      default:
        return <Activity className="size-4" />;
    }
  };

  const exportAuditLogs = () => {
    // Simulate audit log export
    logDebug('Exporting audit logs', {
      logCount: filteredLogs.length,
      filter,
      timestamp: new Date().toISOString(),
    });
  };

  const uniqueUsers = [...new Set(auditLogs.map(log => ({ id: log.userId, name: log.userName })))];
  const uniqueOrganizations = [
    ...new Set(
      auditLogs.map(log => ({
        id: log.organizationId,
        name: log.organizationName,
      }))
    ),
  ];

  if (!hasPermission('admin.audit.view')) {
    return (
      <AdminLayout title="Access Denied">
        <div className="py-8 text-center">
          <p>You don't have permission to view audit logs.</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Audit Logs"
      description="Monitor and review system activity and security events"
    >
      <div className="space-y-6">
        {/* Header Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search audit logs..."
                value={filter.search}
                onChange={e => setFilter({ ...filter, search: e.target.value })}
                className="w-64 pl-10"
              />
            </div>

            <Select
              value={filter.category}
              onValueChange={value => setFilter({ ...filter, category: value })}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="authentication">Authentication</SelectItem>
                <SelectItem value="authorization">Authorization</SelectItem>
                <SelectItem value="data">Data</SelectItem>
                <SelectItem value="system">System</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filter.severity}
              onValueChange={value => setFilter({ ...filter, severity: value })}
            >
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severity</SelectItem>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filter.dateRange}
              onValueChange={value => setFilter({ ...filter, dateRange: value })}
            >
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Time Range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1h">Last Hour</SelectItem>
                <SelectItem value="24h">Last 24h</SelectItem>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="all">All Time</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center space-x-3">
            {hasPermission('admin.audit.export') && (
              <Button variant="outline" onClick={exportAuditLogs}>
                <Download className="mr-2 size-4" />
                Export Logs
              </Button>
            )}
            <Button variant="outline">
              <RefreshCw className="mr-2 size-4" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Audit Statistics */}
        {auditStats && (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Events</CardTitle>
                <FileText className="size-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{auditStats.totalEvents.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">All time events</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Today's Events</CardTitle>
                <Calendar className="size-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{auditStats.todayEvents}</div>
                <p className="text-xs text-muted-foreground">Events in last 24h</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Failure Rate</CardTitle>
                <AlertTriangle className="size-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{auditStats.failureRate}%</div>
                <p className="text-xs text-muted-foreground">Failed operations</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Filters</CardTitle>
                <Filter className="size-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{filteredLogs.length}</div>
                <p className="text-xs text-muted-foreground">Filtered results</p>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs defaultValue="logs" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="logs">Audit Logs</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
            <TabsTrigger value="alerts">Security Alerts</TabsTrigger>
          </TabsList>

          <TabsContent value="logs" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Audit Events ({filteredLogs.length})</CardTitle>
                <CardDescription>Detailed system activity and security events</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12"></TableHead>
                      <TableHead>Timestamp</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Resource</TableHead>
                      <TableHead>Organization</TableHead>
                      <TableHead>Severity</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={8} className="py-8 text-center">
                          Loading audit logs...
                        </TableCell>
                      </TableRow>
                    ) : filteredLogs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="py-8 text-center">
                          No audit logs found matching your criteria
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredLogs.map(log => (
                        <TableRow key={log.id} className="cursor-pointer hover:bg-gray-50">
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              {getStatusIcon(log.status)}
                              {getCategoryIcon(log.category)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <div className="text-sm font-medium">
                                {format(new Date(log.timestamp), 'MMM dd, HH:mm:ss')}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {formatDistanceToNow(new Date(log.timestamp), {
                                  addSuffix: true,
                                })}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <div className="font-medium">{log.userName}</div>
                              <div className="text-sm text-muted-foreground">{log.userEmail}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <code className="rounded bg-gray-100 px-2 py-1 text-sm">
                              {log.action}
                            </code>
                          </TableCell>
                          <TableCell>{log.resource}</TableCell>
                          <TableCell>{log.organizationName}</TableCell>
                          <TableCell>{getSeverityBadge(log.severity)}</TableCell>
                          <TableCell>
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setSelectedLog(log)}
                                >
                                  <Eye className="size-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-2xl">
                                <DialogHeader>
                                  <DialogTitle>Audit Log Details</DialogTitle>
                                  <DialogDescription>
                                    Detailed information about this audit event
                                  </DialogDescription>
                                </DialogHeader>
                                <ScrollArea className="max-h-96">
                                  <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                      <div>
                                        <label className="text-sm font-medium">Timestamp</label>
                                        <p className="text-sm text-muted-foreground">
                                          {format(new Date(log.timestamp), 'PPpp')}
                                        </p>
                                      </div>
                                      <div>
                                        <label className="text-sm font-medium">User</label>
                                        <p className="text-sm text-muted-foreground">
                                          {log.userName} ({log.userEmail})
                                        </p>
                                      </div>
                                      <div>
                                        <label className="text-sm font-medium">Action</label>
                                        <p className="text-sm text-muted-foreground">
                                          {log.action}
                                        </p>
                                      </div>
                                      <div>
                                        <label className="text-sm font-medium">Resource</label>
                                        <p className="text-sm text-muted-foreground">
                                          {log.resource} {log.resourceId && `(${log.resourceId})`}
                                        </p>
                                      </div>
                                      <div>
                                        <label className="text-sm font-medium">IP Address</label>
                                        <p className="text-sm text-muted-foreground">
                                          {log.ipAddress}
                                        </p>
                                      </div>
                                      <div>
                                        <label className="text-sm font-medium">Organization</label>
                                        <p className="text-sm text-muted-foreground">
                                          {log.organizationName}
                                        </p>
                                      </div>
                                    </div>

                                    <div>
                                      <label className="text-sm font-medium">User Agent</label>
                                      <p className="mt-1 rounded bg-gray-100 p-2 font-mono text-sm text-xs text-muted-foreground">
                                        {log.userAgent}
                                      </p>
                                    </div>

                                    <div>
                                      <label className="text-sm font-medium">Event Details</label>
                                      <pre className="mt-1 overflow-auto rounded bg-gray-100 p-3 text-xs">
                                        {JSON.stringify(log.details, null, 2)}
                                      </pre>
                                    </div>
                                  </div>
                                </ScrollArea>
                              </DialogContent>
                            </Dialog>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="analytics" className="space-y-6">
            {auditStats && (
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle>Top Actions</CardTitle>
                    <CardDescription>Most frequent system actions</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {auditStats.topActions.map((action, index) => (
                        <div key={action.action} className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="flex size-6 items-center justify-center rounded-full bg-blue-100 text-xs font-medium">
                              {index + 1}
                            </div>
                            <code className="text-sm">{action.action}</code>
                          </div>
                          <span className="font-medium">{action.count}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Most Active Users</CardTitle>
                    <CardDescription>Users with highest activity</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {auditStats.topUsers.map((user, index) => (
                        <div key={user.userName} className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="flex size-6 items-center justify-center rounded-full bg-green-100 text-xs font-medium">
                              {index + 1}
                            </div>
                            <span className="text-sm">{user.userName}</span>
                          </div>
                          <span className="font-medium">{user.count} events</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          <TabsContent value="alerts" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <AlertTriangle className="size-5 text-red-600" />
                  <span>Security Alerts</span>
                </CardTitle>
                <CardDescription>High-priority security events requiring attention</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {filteredLogs
                    .filter(log => log.severity === 'critical' || log.status === 'failure')
                    .slice(0, 10)
                    .map(log => (
                      <div
                        key={log.id}
                        className="flex items-start space-x-3 rounded-lg border border-red-200 bg-red-50 p-3"
                      >
                        <AlertTriangle className="mt-0.5 size-5 text-red-600" />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium text-red-900">{log.action}</h4>
                            <span className="text-xs text-red-600">
                              {formatDistanceToNow(new Date(log.timestamp), {
                                addSuffix: true,
                              })}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-red-700">
                            {log.userName} - {log.organizationName}
                          </p>
                          <p className="mt-1 text-xs text-red-600">
                            IP: {log.ipAddress} | Resource: {log.resource}
                          </p>
                        </div>
                      </div>
                    ))}

                  {filteredLogs.filter(
                    log => log.severity === 'critical' || log.status === 'failure'
                  ).length === 0 && (
                    <div className="py-8 text-center text-muted-foreground">
                      <CheckCircle className="mx-auto mb-2 size-12 text-green-600" />
                      <p>No critical security alerts at this time</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
